import { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api, waLink, isValidPhone, errText, fmtDate } from "@/lib/api";
import {
  trackOtpVerified, trackChatOpened, trackChatFlowSelected,
  trackBookingCompleted, trackInsuranceInquiry,
  trackPrizeWon, trackGrandPrizeWon, trackCouponClaimedWhatsapp,
} from "@/lib/pixel";
import { ScratchCard } from "./ScratchCard";

const MENU = [
  { label: "🎮 Play & Win", value: "play" },
  { label: "🔧 Book a Service", value: "book" },
  { label: "🛡️ Insurance Claim Help", value: "insurance" },
  { label: "❓ Why Genuine Parts Matter", value: "why" },
];
const CARS = ["Maruti", "Hyundai", "Tata", "Honda", "Toyota", "Other"];
const SERVICES = ["Periodic Service", "AC", "Denting & Painting", "Wheel Care", "Wash", "Other"];
const DAYS = ["Today", "Tomorrow", "This weekend", "Pick later"];
const DUE = ["This week", "This month", "Just exploring"];

const WHY_TEXT =
  "Most garages fit white-labeled or duplicate parts that fail early and void warranties. AutoHQ fits only authentic manufacturer parts with verifiable part numbers, shows you photo proof before fitting, and returns your old parts. 🔩✅";

let msgId = 0;

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [inputMode, setInputMode] = useState(null); // text | phone | otp | free
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const flowRef = useRef({ flow: null, step: null, data: {} });
  const sessionRef = useRef(crypto.randomUUID());
  const startedRef = useRef(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, quickReplies]);

  const log = (role, text, unanswered = false) => {
    api.post("/chat/message", { session_id: sessionRef.current, role, text, unanswered }).catch(() => {});
  };

  const addBot = useCallback((text, opts = {}) => {
    setMessages((m) => [...m, { id: ++msgId, role: "bot", text, link: opts.link, scratch: opts.scratch }]);
    setQuickReplies(opts.buttons || []);
    setInputMode(opts.input || null);
    log("bot", text);
  }, []);

  const addUser = (text) => {
    setMessages((m) => [...m, { id: ++msgId, role: "user", text }]);
    log("user", text);
  };

  const showMenu = useCallback((greeting) => {
    addBot(greeting || "What else can I help you with?", { buttons: MENU, input: "free" });
  }, [addBot]);

  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    addBot("👋 Welcome to AutoHQ! Want to play a quick game and win a free service reward? Takes 30 seconds.", {
      buttons: MENU,
      input: "free",
    });
  }, [addBot]);

  // Auto-open after 5 seconds
  useEffect(() => {
    if (sessionStorage.getItem("ahq_chat_seen")) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("ahq_chat_seen", "1");
      setOpen(true);
      start();
    }, 10000);
    return () => clearTimeout(t);
  }, [start]);

  const openChat = () => {
    setOpen(true);
    sessionStorage.setItem("ahq_chat_seen", "1");
    trackChatOpened({ trigger: "manual" });
    start();
  };

  const f = flowRef.current;

  const askMobile = () => {
    f.step = "mobile";
    addBot("Got it! What's your 10-digit mobile number? We'll send a quick OTP to verify.", { input: "phone" });
  };

  const sendOtp = async (phone) => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/send", { phone });
      f.data.phone = phone;
      f.step = "otp";
      addBot(
        data.mock
          ? "OTP sent! 📲 Enter the 6-digit code. (Dev mode: use 123456)"
          : "OTP sent! 📲 Enter the 6-digit code from the SMS.",
        { input: "otp" }
      );
    } catch (e) {
      addBot(errText(e), { input: "phone" });
    } finally {
      setBusy(false);
    }
  };

  const finishPlay = async () => {
    try {
      const { data } = await api.post("/scratch", {
        phone: f.data.phone, name: f.data.name, car: f.data.car,
        verification_token: f.data.token,
      });
      if (data.already_rewarded) {
        addBot(
          `You've already got a reward waiting! 🎁 Your code: ${data.coupon_code} (${data.prize}), valid till ${fmtDate(data.expires_at)}. Want to book a slot to redeem it?`,
          { link: { href: waLink(`Hi AutoHQ, I'd like to book a slot to redeem my code ${data.coupon_code}. I'm ${f.data.name} and my car is a ${f.data.car}.`), label: "Book on WhatsApp" } }
        );
        showMenu();
        f.flow = null;
        return;
      }
      f.data.reward = data;
      f.step = "scratch";
      setMessages((m) => [...m, { id: ++msgId, role: "bot", text: "Here's your scratch card! 👇 Swipe to reveal your prize.", scratch: data }]);
      setQuickReplies([]);
      setInputMode(null);
      log("bot", "Scratch card shown");
    } catch (e) {
      addBot(errText(e));
      showMenu();
    }
  };

  const onScratchRevealed = () => {
    const r = f.data.reward;
    trackPrizeWon({ prize: r.prize, source: "Chat Game", car_brand: f.data.car, grand_prize: r.grand_prize });
    if (r.grand_prize) {
      trackGrandPrizeWon({ prize: r.prize, source: "Chat Game", car_brand: f.data.car });
    }
    addBot(
      `🎉 You won ${r.prize}! Your code ${r.coupon_code} is valid till ${fmtDate(r.expires_at)} and has been sent via SMS.`,
      { link: { href: waLink(`Hi AutoHQ, I won ${r.prize}, my code is ${r.coupon_code}. I'm ${f.data.name} and my car is a ${f.data.car}.`), label: "Book on WhatsApp", waEventData: { prize: r.prize, source: "Chat Game", coupon_code: r.coupon_code } } }
    );
    setTimeout(() => {
      f.step = "due";
      addBot("One last thing — when is your car due for service?", {
        buttons: DUE.map((d) => ({ label: d, value: d })),
      });
    }, 800);
  };

  const verifyOtp = async (otp) => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/verify", { phone: f.data.phone, otp });
      f.data.token = data.verification_token;
      trackOtpVerified({ source: `Chat ${f.flow}`, phone: f.data.phone, name: f.data.name });
      api.post("/chat/link", { session_id: sessionRef.current, phone: f.data.phone }).catch(() => {});

      if (f.flow === "play") {
        await finishPlay();
      } else if (f.flow === "book") {
        await api.post("/booking", {
          phone: f.data.phone, name: f.data.name, car: f.data.car,
          service_needed: f.data.service, preferred_day: f.data.day,
          verification_token: f.data.token,
        });
        trackBookingCompleted({ service_needed: f.data.service, car_brand: f.data.car, preferred_day: f.data.day });
        addBot("✅ Booked! Our advisor will confirm on WhatsApp within 30 minutes.");
        f.flow = null;
        showMenu();
      } else if (f.flow === "insurance") {
        await api.post("/insurance", {
          phone: f.data.phone, name: f.data.name, car: f.data.car,
          insurer: f.data.insurer, verification_token: f.data.token,
        });
        trackInsuranceInquiry({ insurer: f.data.insurer, car_brand: f.data.car });
        addBot("🛡️ Done! Our claims expert will call you within the hour.");
        f.flow = null;
        showMenu();
      }
    } catch (e) {
      addBot(errText(e), { input: "otp" });
    } finally {
      setBusy(false);
    }
  };

  const handleValue = async (value) => {
    // Menu selections
    if (!f.flow || f.step === null) {
      if (value === "play") {
        f.flow = "play"; f.step = "name"; f.data = {};
        trackChatFlowSelected("play");
        addBot("Awesome! 🎮 What's your name?", { input: "text" });
        return;
      }
      if (value === "book") {
        f.flow = "book"; f.step = "name"; f.data = {};
        trackChatFlowSelected("book");
        addBot("Great! What's your name?", { input: "text" });
        return;
      }
      if (value === "insurance") {
        f.flow = "insurance"; f.step = "name"; f.data = {};
        trackChatFlowSelected("insurance");
        addBot("We'll get your claim sorted fast. What's your name?", { input: "text" });
        return;
      }
      if (value === "why") {
        trackChatFlowSelected("why");
        addBot(WHY_TEXT, {
          buttons: [
            { label: "🎮 Play & Win", value: "play" },
            { label: "🔧 Book a Service", value: "book" },
          ],
          input: "free",
        });
        return;
      }
    }

    switch (`${f.flow}:${f.step}`) {
      case "play:name":
        f.data.name = value; f.step = "car";
        addBot(`Nice to meet you, ${value.split(" ")[0]}! Which car do you drive?`, {
          buttons: CARS.map((c) => ({ label: c, value: c })),
        });
        break;
      case "play:car":
        f.data.car = value;
        askMobile();
        break;
      case "play:due":
        api.post("/leads/service-due", { phone: f.data.phone, answer: value }).catch(() => {});
        addBot("Perfect, noted! 📝 See you at AutoHQ Ranigunj. Don't forget your code expires in 14 days!");
        f.flow = null;
        showMenu();
        break;

      case "book:name":
        f.data.name = value; f.step = "car";
        addBot("Which car do you drive?", { buttons: CARS.map((c) => ({ label: c, value: c })) });
        break;
      case "book:car":
        f.data.car = value; f.step = "service";
        addBot("What service do you need?", { buttons: SERVICES.map((s) => ({ label: s, value: s })) });
        break;
      case "book:service":
        f.data.service = value; f.step = "day";
        addBot("When would you like to come in?", { buttons: DAYS.map((d) => ({ label: d, value: d })) });
        break;
      case "book:day":
        f.data.day = value;
        askMobile();
        break;

      case "insurance:name":
        f.data.name = value; f.step = "car";
        addBot("Which car is the claim for?", { buttons: CARS.map((c) => ({ label: c, value: c })) });
        break;
      case "insurance:car":
        f.data.car = value; f.step = "insurer";
        addBot("Who is your insurer? (e.g. ICICI Lombard, Bajaj Allianz...)", { input: "text" });
        break;
      case "insurance:insurer":
        f.data.insurer = value;
        askMobile();
        break;

      default:
        if (f.step === "mobile") {
          if (!isValidPhone(value)) {
            addBot("Hmm, that doesn't look like a valid 10-digit Indian mobile number. Try again?", { input: "phone" });
            return;
          }
          await sendOtp(value);
        } else if (f.step === "otp") {
          if (!/^\d{6}$/.test(value)) {
            addBot("The OTP is 6 digits. Try again?", { input: "otp" });
            return;
          }
          await verifyOtp(value);
        } else {
          // Fallback for unhandled free text
          log("user_unanswered", value, true);
          addBot("Let me connect you with our team on WhatsApp — they reply fast! 👇", {
            link: { href: waLink(`Hi AutoHQ! ${value}`), label: "Chat on WhatsApp" },
            buttons: MENU,
            input: "free",
          });
        }
    }
  };

  const onQuickReply = (btn) => {
    if (busy) return;
    setQuickReplies([]);
    addUser(btn.label);
    handleValue(btn.value);
  };

  const onSend = () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    addUser(text);
    if (inputMode === "free" || inputMode === null) {
      log("user_unanswered", text, true);
      addBot("Let me connect you with our team on WhatsApp — they reply fast! 👇", {
        link: { href: waLink(`Hi AutoHQ! ${text}`), label: "Chat on WhatsApp" },
        buttons: quickReplies.length ? quickReplies : MENU,
        input: "free",
      });
    } else {
      handleValue(text);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-bubble-button"
          onClick={openChat}
          className="fixed bottom-[88px] right-4 z-40 w-14 h-14 rounded-full bg-[#FF5A00] flex items-center justify-center shadow-lg hover:scale-105 transition-transform bubble-pulse"
          aria-label="Open AutoHQ chat"
        >
          <MessageCircle className="w-7 h-7 text-white" />
        </button>
      )}

      {open && (
        <div
          className="chat-panel fixed bottom-16 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[75vh] bg-white border border-[#E4E4E7] rounded-lg flex flex-col overflow-hidden shadow-2xl"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#FF5A00]">
            <div>
              <p className="font-extrabold text-sm text-white">
                AutoHQ Assistant
              </p>
              <p className="text-[11px] text-white/85">● Online — replies instantly</p>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#FF5A00] text-white rounded-br-sm"
                      : "bg-[#F5F5F7] text-[#1A1A1A] rounded-bl-sm"
                  }`}
                >
                  {m.text}
                  {m.scratch && (
                    <div className="mt-3">
                      <ScratchCard prize={m.scratch.prize} code={m.scratch.coupon_code} onRevealed={onScratchRevealed} />
                    </div>
                  )}
                  {m.link && (
                    <a
                      href={m.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => m.link.waEventData && trackCouponClaimedWhatsapp(m.link.waEventData)}
                      className="mt-2 block text-center bg-[#25D366] hover:bg-[#1fb457] text-white font-bold text-xs rounded-lg py-2 px-3"
                    >
                      {m.link.label}
                    </a>
                  )}
                </div>
              </div>
            ))}
            {quickReplies.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {quickReplies.map((b) => (
                  <button
                    key={b.value}
                    data-testid={`chat-quick-reply-${b.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => onQuickReply(b)}
                    className="text-xs font-semibold border border-[#FF5A00]/60 text-[#FF5A00] hover:bg-[#FF5A00] hover:text-white rounded-full px-3.5 py-2 transition-colors"
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#E4E4E7] bg-white flex gap-2">
            <input
              data-testid="chat-text-input"
              value={input}
              onChange={(e) => {
                const v = e.target.value;
                setInput(inputMode === "phone" || inputMode === "otp" ? v.replace(/\D/g, "") : v);
              }}
              onKeyDown={(e) => e.key === "Enter" && onSend()}
              maxLength={inputMode === "phone" ? 10 : inputMode === "otp" ? 6 : 200}
              inputMode={inputMode === "phone" || inputMode === "otp" ? "numeric" : "text"}
              placeholder={
                inputMode === "phone" ? "10-digit mobile number"
                : inputMode === "otp" ? "6-digit OTP"
                : inputMode === "text" ? "Type your answer..."
                : "Type a message..."
              }
              className="flex-1 bg-[#F5F5F7] border border-[#E4E4E7] rounded-lg px-3 py-2.5 text-sm text-[#0A0A0A] focus:border-[#FF5A00] focus:outline-none"
            />
            <button
              data-testid="chat-send-button"
              onClick={onSend}
              disabled={busy}
              className="bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-50 rounded-lg px-3.5 flex items-center justify-center"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
